"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { getRegionLocaleHref } from "@/services/region";
import { buildRegisterHref } from "@/modules/auth/utils/loginRedirect";
import FreeTrialProfileFields, {
  type FreeTrialProfileFieldsHandle,
} from "./FreeTrialProfileFields";
import CalendarPicker from "@/components/CalendarPicker";
import { ar, enUS } from "date-fns/locale";
import { addDays, format, isSameDay } from "date-fns";
import { useLocale, useTranslations } from "next-intl";
import FormSelect from "@/components/FormSelect";
import LottieLoader from "@/components/LottieLoader";
import BookingSessionTimer from "@/components/BookingSessionTimer";
import Swal from "sweetalert2";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import { canCreateDependant } from "@/modules/auth/utils/userAccess";
import { buildAttendeeOptions } from "@/modules/booking/utils/attendeeOptions";
import { pushGtmFreeTrialBooking } from "@/lib/gtmFreeTrial";
import { requestMetaPixelFlush } from "@/lib/metaPixel";
import {
  FREE_TRIAL_SUCCESS_PATH,
  completeFreeTrialBooking,
} from "@/modules/booking/utils/freeTrialBookingSuccess";
import { resolveAttendeeTimezone } from "@/modules/booking/utils/attendeeTimezone";
import type { AuthUser } from "@/modules/auth/types";
import {
  confirmFreeTrialBookingApi,
  getFreeTrialAvailabilityApi,
  getFreeTrialLanguagesApi,
  getFreeTrialMetadataApi,
  getFreeTrialSlotsApi,
  type BookingCategoryOption,
  type BookingSelectOption,
  type FreeTrialSlot,
} from "../services/freeTrial.api";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import AddDependantModal from "@/modules/dashboard/components/s/AddDependantModal";
import { formatRawTimeString } from "@/modules/web/utils/timeFormat";
import { partitionByOther } from "@/modules/web/utils/categoryGrouping";

import { Globe } from "lucide-react";

type BookingStep = 1 | 2 | 3;

type BookingState = {
  attendeeId: string;
  /**
   * Every subject the trial is to cover. A trial may now span several in one
   * session, and the assessor has to be able to assess all of them - so adding
   * a subject narrows the teacher pool rather than widening it.
   */
  categoryIds: string[];
  /**
   * The first selection. Kept alongside the list because the "already booked"
   * copy, the review summary and the deep-linked ?category_id all speak in
   * terms of a single subject.
   */
  categoryId: string;
  languageCode: string;
  hearAboutUsId: string;
  hearAboutUsOther: string;
  selectedDate: Date | null;
  selectedSlot: FreeTrialSlot | null;
};

type BookingErrors = Partial<
  Record<
    | "attendeeId"
    | "categoryId"
    | "languageCode"
    | "hearAboutUsId"
    | "hearAboutUsOther"
    | "selectedDate"
    | "selectedSlot",
    string
  >
>;

/**
 * Sentinel option values for the category select's in-place "Other" drill-in.
 * Category values are numeric-string ids, so these never collide with a real
 * category. Selecting OTHER swaps the same dropdown to the `show_in_other`
 * categories; BACK returns to the primary list. Neither is submitted as a
 * categoryId itself.
 */
const OTHER_CATEGORY_VALUE = "__other__";
const BACK_CATEGORY_VALUE = "__back__";

/** Pause between saving step 1's profile and fetching step 2's slots. */
const PROFILE_SETTLE_DELAY_MS = 2000;

const INITIAL_STATE: BookingState = {
  attendeeId: "",
  categoryIds: [],
  categoryId: "",
  languageCode: "",
  hearAboutUsId: "",
  hearAboutUsOther: "",
  selectedDate: null,
  selectedSlot: null,
};

export default function FreeTrialBookingContent() {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const t = useTranslations("web.freeTrial");
  const {
    user,
    loading: userLoading,
    isAuthenticated,
    isUnauthenticated,
    refetchUser,
  } = useCurrentUser();
  const requestedCategoryId = searchParams.get("category_id");

  const [step, setStep] = useState<BookingStep>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [state, setState] = useState<BookingState>(INITIAL_STATE);
  const [errors, setErrors] = useState<BookingErrors>({});
  const [categories, setCategories] = useState<BookingCategoryOption[]>([]);
  // Whether the user drilled into the "Other" group in the category selector.
  // The selected id still lives in state.categoryId; this only controls which
  // option set (primary vs Other) the single select shows.
  const [otherCategoryOpen, setOtherCategoryOpen] = useState(false);
  // The category select's menu is controlled so choosing "Other"/"Back" can
  // keep it open (swapping the options in place) while a real pick closes it.
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  // Languages are scoped to the chosen category (only those a slot-eligible
  // free-trial teacher for that category speaks), fetched by the effect below
  // whenever category/attendee changes — NOT seeded from metadata.
  const [languages, setLanguages] = useState<BookingSelectOption[]>([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [hearAboutUsOptions, setHearAboutUsOptions] = useState<
    BookingSelectOption[]
  >([]);
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [slots, setSlots] = useState<FreeTrialSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  // hasActiveFreeTrial gates the form (persists once known, even after the
  // modal is dismissed); showActiveTrialModal only controls whether the
  // popup itself is currently visible. Closing the popup must not lift the
  // block — the attendee still has an active trial either way.
  const [hasActiveFreeTrial, setHasActiveFreeTrial] = useState(false);
  const [showActiveTrialModal, setShowActiveTrialModal] = useState(false);
  // Holds the attendee id whose metadata fetch is allowed to open the modal:
  // only an explicit pick in the attendee select sets it. Landing on the page
  // shouldn't greet the visitor with a dialog - the inline notice and the
  // disabled fields already say the form is blocked.
  const attendeeAwaitingModalRef = useRef<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // A logged-out visitor used to be redirected straight to /register. They now
  // stay here and are offered "Continue with Google" as the first step of this
  // same wizard, because the point of the change is to stop showing an
  // eleven-field form before a single trial question.
  //
  // `returnToPath` still carries this exact URL (query included, so a
  // deep-linked ?category_id survives) so both the Google round trip and the
  // secondary "sign up with email" link come back to precisely this page.
  const returnToPath =
    pathname + (searchParams.toString() ? `?${searchParams.toString()}` : "");

  // A logged-out visitor goes straight to sign-up rather than being offered an
  // auth step on this page. Booking a free trial is the top of the funnel, so
  // almost everyone arriving here logged out needs an account, and /register
  // is where both routes to one - Google and email - already live.
  //
  // `?redirect=` carries this exact URL (query included, so a deep-linked
  // ?category_id survives) so the new account lands back here once it
  // authenticates.
  //
  // replace(), not push(): the free-trial URL must not sit in history behind
  // the register page, or Back would land on it while still logged out and
  // bounce straight forward again.
  const hasRedirectedToRegisterRef = useRef(false);

  useEffect(() => {
    if (!isUnauthenticated || hasRedirectedToRegisterRef.current) {
      return;
    }

    hasRedirectedToRegisterRef.current = true;

    router.replace(
      buildRegisterHref(
        getRegionLocaleHref(pathname, locale, "/register"),
        returnToPath,
      ),
    );
  }, [isUnauthenticated, locale, pathname, returnToPath, router]);

  // Signed in through Google, but Google could not supply everything
  // registration would have asked for. Those fields are collected inside step 1
  // rather than on a screen of their own - see the profile block there.
  const needsProfile =
    isAuthenticated && user != null && user.profile_completed === false;

  // Lets step 1's "Next" validate and save the embedded profile fields.
  const profileFieldsRef = useRef<FreeTrialProfileFieldsHandle>(null);

  // Teacher eligibility (gender, children-support) is derived server-side from
  // the attendee's saved gender and birthday. Slots must not be fetched until
  // step 1 has saved those, or the backend matches against NULLs - which it
  // treats as "any teacher" - and a male student can be offered a female one.
  // A profile still missing data (gender, country, phone, ...) blocks them too.
  const slotsEnabled = step >= 2 && !needsProfile;
  // Covers the profile save plus the settle delay after it, so "Next" can't be
  // pressed twice while step 2's slots are about to be fetched.
  const [isPreparingSlots, setIsPreparingSlots] = useState(false);
  // Set when the backend refuses a slot request for the same reason - e.g. an
  // older account, or a dependant, with no gender or birthday on file, which
  // `needsProfile` alone cannot see.
  // Holds the attendee it was refused for, so picking someone else lifts it.
  const [profileIncompleteFor, setProfileIncompleteFor] = useState<
    string | null
  >(null);
  const isProfileBlocked =
    needsProfile || profileIncompleteFor === state.attendeeId;

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const updateIsMobile = () => setIsMobile(mediaQuery.matches);
    updateIsMobile();
    mediaQuery.addEventListener("change", updateIsMobile);
    return () => mediaQuery.removeEventListener("change", updateIsMobile);
  }, []);

  /**
   * Sets the subject list and its primary together. Two fields that must agree
   * are easy to desynchronise if each call site sets them itself.
   */
  const setCategoryIds = (ids: string[]) => {
    setState((current) => ({
      ...current,
      categoryIds: ids,
      categoryId: ids[0] ?? "",
      // Changing the subjects changes which assessors are eligible, so any
      // slot already picked no longer necessarily exists.
      selectedSlot: null,
      // The language list is filtered by subject too.
      languageCode: ids.length > 0 ? current.languageCode : "",
    }));
  };

  // Effect-dependency key for the subject selection. The array itself is a new
  // reference whenever state updates, which would re-run every fetch below on
  // unrelated changes; the joined ids only change when the selection does.
  const categoryKey = state.categoryIds.join(",");

  const updateField = <K extends keyof BookingState>(
    field: K,
    value: BookingState[K],
  ) => {
    const shouldResetSlot = [
      "attendeeId",
      "categoryIds",
      "categoryId",
      "languageCode",
      "hearAboutUsId",
      "selectedDate",
    ].includes(field);

    setState((current) => ({
      ...current,
      [field]: value,
      ...(shouldResetSlot ? { selectedSlot: null } : {}),
    }));
    setErrors((current) => ({
      ...current,
      [field]: undefined,
      ...(shouldResetSlot ? { selectedSlot: undefined } : {}),
    }));
  };

  const handleSessionTimeout = () => {
    setStep(1);
    setState(INITIAL_STATE);
  };

  // Keeps whatever region prefix the visitor arrived with (/mena/en/...)
  // instead of dropping them onto the region-less URL.
  const localizedHref = (path: string) =>
    getRegionLocaleHref(pathname, locale, path);

  const isArabic = locale === "ar";
  const slotsRef = useRef<HTMLDivElement>(null);
  // Anchor for the current step's fields, so a failed "Next" can scroll the
  // required-field errors back into view (they're above the button, off-screen
  // on mobile after the user has scrolled down to tap Next).
  const stepContentRef = useRef<HTMLElement>(null);
  const dateLocale = isArabic ? ar : enUS;
  // Earliest selectable: start of today. The old flat 24h-from-now cutoff
  // blocked every short-lead-time (SLT) assessor's own, shorter minimum
  // notice window (as low as 30 minutes) from ever being reachable in the
  // date picker — the backend (SchedulingService::filterAndFormatAvailableSlots)
  // is what actually decides, per teacher, which slots on a given day are
  // bookable, so the picker only needs to stop the student from picking a
  // day that's already fully in the past.
  const minDate = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return startOfToday;
  }, []);
  // Latest selectable: 30 days from now
  const maxDate = useMemo(
    () => new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    [],
  );

  const tSwitch = useTranslations("studentSwitchAccount");

  const attendeeOptions = useMemo(() => {
    const options = buildAttendeeOptions(user, t("you_indicator"));
    const canAddDependant = canCreateDependant(user);
    if (canAddDependant) {
      options.push({
        value: "ADD_DEPENDANT",
        label: `+ ${tSwitch("add_dependant")}`,
      });
    }
    return options;
  }, [user, t, tSwitch]);

  useEffect(() => {
    if (!isAuthenticated) {
      setMetadataLoading(false);
      return;
    }

    let active = true;

    const loadMetadata = async () => {
      setMetadataLoading(true);
      setMetadataError(null);

      try {
        const response = await getFreeTrialMetadataApi(
          locale,
          state.attendeeId || undefined,
        );

        if (!active) {
          return;
        }

        setCategories(response.categories);
        // response.languages (every active language) is intentionally ignored:
        // the language picker is driven by the category-scoped effect below.
        setHearAboutUsOptions(response.hearAboutUs);
        // Re-evaluated per attendee: switching to a different dependant who
        // has no active trial must lift the block and clear the modal shown
        // for the previous one.
        setHasActiveFreeTrial(response.hasActiveFreeTrial);

        // The modal is an answer to "I just picked this person" - it opens
        // only for the attendee the visitor actually selected, not for
        // whoever the select happens to be seeded with on load, and not for
        // the refetches triggered by a locale change or a fresh booking.
        const answersAttendeePick =
          attendeeAwaitingModalRef.current !== null &&
          attendeeAwaitingModalRef.current === state.attendeeId;
        attendeeAwaitingModalRef.current = null;
        setShowActiveTrialModal(
          response.hasActiveFreeTrial && answersAttendeePick,
        );

        // Reset category if it's now booked or invalid
        setState((current) => {
          const requestedCategory = response.categories.find(
            (category) =>
              category.value === requestedCategoryId && !category.isBooked,
          );
          // Keep only subjects this attendee can still book; a deep-linked
          // ?category_id seeds the selection when nothing valid is chosen yet.
          const stillValid = current.categoryIds.filter((id) =>
            response.categories.some(
              (category) => category.value === id && !category.isBooked,
            ),
          );

          const next =
            stillValid.length === 0 && requestedCategory
              ? [requestedCategory.value]
              : stillValid;

          if (
            next.length === current.categoryIds.length &&
            next.every((id, index) => id === current.categoryIds[index])
          ) {
            return current;
          }

          return { ...current, categoryIds: next, categoryId: next[0] ?? "" };
        });
      } catch {
        if (!active) {
          return;
        }

        setMetadataError(t("metadata_failed"));
        setCategories([]);
        setLanguages([]);
        setHearAboutUsOptions([]);
        setHasActiveFreeTrial(false);
        setShowActiveTrialModal(false);
      } finally {
        if (active) {
          setMetadataLoading(false);
        }
      }
    };

    void loadMetadata();

    return () => {
      active = false;
    };
  }, [
    isAuthenticated,
    locale,
    requestedCategoryId,
    t,
    state.attendeeId,
  ]);

  // Category-scoped language options. A free trial can only be booked in a
  // language spoken by a slot-eligible teacher for the chosen category, so the
  // picker is (re)fetched whenever category/attendee changes rather than seeded
  // with every active language — this is what stops a language that yields zero
  // slots from ever being offered. Cleared while no category is chosen yet.
  useEffect(() => {
    if (!isAuthenticated || !state.attendeeId || !state.categoryId) {
      setLanguages([]);
      setLanguagesLoading(false);
      return;
    }

    let active = true;
    setLanguagesLoading(true);

    getFreeTrialLanguagesApi({
      attendeeId: state.attendeeId,
      categoryId: state.categoryId,
      locale,
    })
      .then((options) => {
        if (!active) {
          return;
        }
        setLanguages(options);
        // Drop a previously-picked language this category doesn't offer.
        setState((current) =>
          current.languageCode &&
          !options.some((option) => option.value === current.languageCode)
            ? { ...current, languageCode: "" }
            : current,
        );
      })
      .catch(() => {
        if (active) {
          setLanguages([]);
        }
      })
      .finally(() => {
        if (active) {
          setLanguagesLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [isAuthenticated, state.attendeeId, categoryKey, locale]);

  useEffect(() => {
    if (attendeeOptions.length === 0) {
      return;
    }

    setState((current) => {
      if (
        current.attendeeId &&
        attendeeOptions.some((option) => option.value === current.attendeeId)
      ) {
        return current;
      }

      const currentUserOpt = attendeeOptions.find(
        (option) => option.isCurrentUser,
      );
      return {
        ...current,
        attendeeId: currentUserOpt
          ? currentUserOpt.value
          : (attendeeOptions[0]?.value ?? ""),
      };
    });
  }, [attendeeOptions]);

  useEffect(() => {
    if (
      !slotsEnabled ||
      !state.selectedDate ||
      !state.attendeeId ||
      !state.categoryId ||
      !state.languageCode ||
      !state.hearAboutUsId
    ) {
      setSlots([]);
      setSlotsLoading(false);
      setSlotsError(null);
      return;
    }

    let active = true;

    const loadSlots = async () => {
      setSlotsLoading(true);
      setSlotsError(null);

      try {
        const response = await getFreeTrialSlotsApi({
          attendeeId: state.attendeeId,
          categoryId: state.categoryId,
          languageCode: state.languageCode,
          hearAboutUsId: state.hearAboutUsId,
          hearAboutUsOther: state.hearAboutUsOther,
          selectedDate: format(state.selectedDate as Date, "yyyy-MM-dd"),
          locale,
        });

        if (!active) {
          return;
        }

        setSlots(response);
        setState((current) => ({
          ...current,
          selectedSlot: response.some(
            (slot) =>
              current.selectedSlot &&
              slot.start === current.selectedSlot.start &&
              slot.end === current.selectedSlot.end,
          )
            ? current.selectedSlot
            : null,
        }));
      } catch (error) {
        if (!active) {
          return;
        }

        if (isProfileIncompleteError(error)) {
          setProfileIncompleteFor(state.attendeeId);
        }
        setSlots([]);
        setState((current) => ({
          ...current,
          selectedSlot: null,
        }));
        setSlotsError(t("slots_failed"));
      } finally {
        if (active) {
          setSlotsLoading(false);
        }
      }
    };

    void loadSlots();

    return () => {
      active = false;
    };
  }, [
    slotsEnabled,
    state.attendeeId,
    categoryKey,
    state.hearAboutUsId,
    state.languageCode,
    state.selectedDate,
    t,
  ]);

  // Default view: a single-day "strip" over just today/tomorrow/the day
  // after, matching the booking page's calendar redesign - most trials are
  // booked for one of these three days, and a short-lead-time teacher can
  // now be booked as soon as today (see minDate above), so this is usually
  // all a student needs to see. "Full Month" switches to the existing
  // CalendarPicker grid for anyone who wants to look further ahead.
  const [calendarView, setCalendarView] = useState<"strip" | "month">("strip");
  const [dayStripIndex, setDayStripIndex] = useState(0);
  const [dayStripSlots, setDayStripSlots] = useState<
    Record<string, FreeTrialSlot[]>
  >({});
  const dayKey = (date: Date) => format(date, "yyyy-MM-dd");
  const stripDates = useMemo(
    () => [0, 1, 2].map((offset) => addDays(minDate, offset)),
    [minDate],
  );
  // Only auto-picks the earliest available strip day once per metadata
  // load - re-running this every time slots refresh would otherwise fight a
  // student who deliberately picked a later, still-available day.
  const autoSelectedStripRef = useRef(false);

  useEffect(() => {
    autoSelectedStripRef.current = false;
    setDayStripSlots({});
    setDayStripIndex(0);
  }, [state.attendeeId, categoryKey, state.languageCode]);

  useEffect(() => {
    if (
      !slotsEnabled ||
      !state.attendeeId ||
      !state.categoryId ||
      !state.languageCode ||
      !state.hearAboutUsId
    ) {
      return;
    }

    let active = true;

    const loadStrip = async () => {
      const results = await Promise.all(
        stripDates.map((date) =>
          getFreeTrialSlotsApi({
            attendeeId: state.attendeeId,
            categoryId: state.categoryId,
            languageCode: state.languageCode,
            hearAboutUsId: state.hearAboutUsId,
            hearAboutUsOther: state.hearAboutUsOther,
            selectedDate: dayKey(date),
            locale,
          }).catch((error) => {
            if (active && isProfileIncompleteError(error)) {
              setProfileIncompleteFor(state.attendeeId);
            }

            return [];
          }),
        ),
      );

      if (!active) {
        return;
      }

      const byDate: Record<string, FreeTrialSlot[]> = {};
      stripDates.forEach((date, index) => {
        byDate[dayKey(date)] = results[index];
      });
      setDayStripSlots(byDate);

      if (!autoSelectedStripRef.current && !state.selectedDate) {
        const earliestIndex = stripDates.findIndex(
          (date) => byDate[dayKey(date)]?.length,
        );
        if (earliestIndex !== -1) {
          autoSelectedStripRef.current = true;
          setDayStripIndex(earliestIndex);
          updateField("selectedDate", stripDates[earliestIndex]);
        }
      }
    };

    void loadStrip();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    slotsEnabled,
    state.attendeeId,
    categoryKey,
    state.languageCode,
    state.hearAboutUsId,
    state.hearAboutUsOther,
    locale,
  ]);

  // Per-day bookability for the "Full Month" CalendarPicker, keyed by
  // 'yyyy-MM-dd' - lets the calendar gray out a day before the student ever
  // clicks it, instead of only discovering it's empty after picking it and
  // fetching its slots (see getFreeTrialAvailabilityApi()). Deliberately
  // scoped to calendarView === "month": the strip view above already
  // prefetches each of its 3 days' real slot lists directly, so there is
  // nothing this would add there. Kept null on load-in-progress or on
  // fetch failure so every in-range day stays clickable exactly as it did
  // before this existed - the actual slot search after picking a day
  // remains the real, authoritative gate either way.
  const [monthAvailability, setMonthAvailability] = useState<Record<
    string,
    boolean
  > | null>(null);

  useEffect(() => {
    if (
      !slotsEnabled ||
      calendarView !== "month" ||
      !state.attendeeId ||
      !state.categoryId ||
      !state.languageCode
    ) {
      return;
    }

    let active = true;
    setMonthAvailability(null);

    getFreeTrialAvailabilityApi({
      attendeeId: state.attendeeId,
      categoryId: state.categoryId,
      languageCode: state.languageCode,
      startDate: dayKey(minDate),
      endDate: dayKey(maxDate),
      locale,
    })
      .then((availability) => {
        if (active) {
          setMonthAvailability(availability);
        }
      })
      .catch(() => {
        if (active) {
          setMonthAvailability(null);
        }
      });

    return () => {
      active = false;
    };
  }, [
    slotsEnabled,
    calendarView,
    state.attendeeId,
    categoryKey,
    state.languageCode,
    minDate,
    maxDate,
    locale,
  ]);

  const earliestStripDate = useMemo(() => {
    return (
      stripDates.find((date) => dayStripSlots[dayKey(date)]?.length) ?? null
    );
  }, [stripDates, dayStripSlots]);

  const selectStripDay = (index: number) => {
    const date = stripDates[index];
    const knownSlots = dayStripSlots[dayKey(date)];

    // Once this day's slots have actually been fetched (every strip day is
    // prefetched - see the effect above), an empty result means every slot
    // is booked - the day itself is never selectable, matching the legacy
    // "No Available Slots" gate instead of silently switching to it and
    // showing an empty grid.
    if (knownSlots && knownSlots.length === 0) {
      setDayStripIndex(index);
      // Blocks the switch, but the previously selected day's slots must not
      // linger behind the alert either - clearing the selection empties the
      // grid instead of showing an unrelated day's times under it.
      updateField("selectedDate", null);
      Swal.fire({
        icon: "warning",
        title: t("no_available_slots_title"),
        text: t("no_available_slots_message"),
        confirmButtonText: "OK",
        confirmButtonColor: "var(--primary)",
      });
      return;
    }

    setDayStripIndex(index);
    updateField("selectedDate", date);
  };

  const attendeeTimezone = useMemo(
    () => resolveAttendeeTimezone(user, state.attendeeId),
    [state.attendeeId, user],
  );

  const selectedAttendee = attendeeOptions.find(
    (option) => option.value === state.attendeeId,
  );
  const selectedCategory = categories.find(
    (option) => option.value === state.categoryId,
  );
  // Primary categories render directly; "Other" ones sit behind the synthetic
  // Other entry. Backend already orders by position, so the split preserves it.
  const { primary: primaryCategories, other: otherCategories } =
    partitionByOther(categories);
  const isSelectedCategoryOther = otherCategories.some(
    (option) => option.value === state.categoryId,
  );
  // The single category select drills into the Other group in place: when
  // `inOtherView` the same dropdown lists the Other categories (plus a Back
  // entry) instead of the primary ones. Driven either by the user opening Other
  // or by the current selection (e.g. a ?category_id= deep link) being an Other
  // category, so its value always has a matching option to display.
  const inOtherView =
    otherCategories.length > 0 &&
    (otherCategoryOpen || isSelectedCategoryOther);
  const selectedLanguage = languages.find(
    (option) => option.value === state.languageCode,
  );
  const selectedHearAboutUs = hearAboutUsOptions.find(
    (option) => option.value === state.hearAboutUsId,
  );
  const isHearAboutUsOther = selectedHearAboutUs?.isOther ?? false;

  // On a failed "Next", bring the current step's fields (and their newly shown
  // required-field errors) back into view, accounting for the sticky navbar.
  const scrollToStepTop = () => {
    const el = stepContentRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: top < 0 ? 0 : top, behavior: "smooth" });
  };

  const handleNext = async () => {
    if (hasActiveFreeTrial) {
      return;
    }

    if (step === 1) {
      const nextErrors: BookingErrors = {};

      if (!state.attendeeId) nextErrors.attendeeId = t("validation.attendee");
      if (state.categoryIds.length === 0)
        nextErrors.categoryId = t("validation.category");
      if (!state.languageCode)
        nextErrors.languageCode = t("validation.language");
      if (!state.hearAboutUsId)
        nextErrors.hearAboutUsId = t("validation.hear_about_us");
      if (isHearAboutUsOther && !state.hearAboutUsOther.trim())
        nextErrors.hearAboutUsOther = t("validation.hear_about_us_other");

      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
        scrollToStepTop();
        return;
      }

      // The profile fields are part of this step, so they have to pass before
      // it does. Run them only after the booking fields are known good, so a
      // visitor who missed both sees the booking errors first rather than
      // having the page save a profile for a step that was not valid anyway.
      if (needsProfile) {
        setIsPreparingSlots(true);

        try {
          const saved = await profileFieldsRef.current?.submitProfile();

          if (!saved) {
            scrollToStepTop();
            return;
          }

          // Give the just-saved gender/birthday a moment to settle before
          // step 2 asks for slots matched against them, and reload the user so
          // step 2 opens already knowing the profile is complete.
          await Promise.all([
            refetchUser(),
            new Promise((resolve) =>
              setTimeout(resolve, PROFILE_SETTLE_DELAY_MS),
            ),
          ]);
        } finally {
          setIsPreparingSlots(false);
        }
      }

      setErrors({});
      setStep(2);
      return;
    }

    if (step === 2) {
      const nextErrors: BookingErrors = {};

      if (!state.selectedDate) nextErrors.selectedDate = t("validation.date");
      if (!state.selectedSlot) nextErrors.selectedSlot = t("validation.slot");

      if (Object.keys(nextErrors).length > 0) {
        setErrors((current) => ({
          ...current,
          ...nextErrors,
        }));
        scrollToStepTop();
        return;
      }

      setErrors((current) => ({
        ...current,
        selectedDate: undefined,
        selectedSlot: undefined,
      }));
      setStep(3);
    }
  };

  // Clears Step 1's own selections. `attendeeId` is blanked with the rest and
  // the attendee-defaulting effect re-selects the current user, which is the
  // same thing a fresh page load shows.
  const resetStep1Data = () => {
    setState((current) => ({
      ...current,
      attendeeId: "",
      categoryId: "",
      languageCode: "",
      hearAboutUsId: "",
      hearAboutUsOther: "",
    }));
    setErrors((current) => ({
      ...current,
      attendeeId: undefined,
      categoryId: undefined,
      languageCode: undefined,
      hearAboutUsId: undefined,
      hearAboutUsOther: undefined,
    }));
  };

  // Clears Step 2's own selections: the chosen day and time slot.
  const resetStep2Data = () => {
    setState((current) => ({
      ...current,
      selectedDate: null,
      selectedSlot: null,
    }));
    setErrors((current) => ({
      ...current,
      selectedDate: undefined,
      selectedSlot: undefined,
    }));
  };

  // Back discards the selections of the step being left AND of the step being
  // returned to, so going back always lands on a clean step instead of one
  // pre-filled with earlier answers. Previously this only changed the step
  // number and kept everything.
  const handlePrevious = () => {
    if (step === 2) {
      resetStep2Data();
      resetStep1Data();
    } else if (step === 3) {
      resetStep2Data();
    }

    setStep((current) => (current === 1 ? 1 : ((current - 1) as BookingStep)));
  };

  // Shared by the initial confirm attempt and the retry after accepting a
  // suggested slot - both just submit whichever teacher/time they're given
  // and, on success, run the same "booked" side effects.
  const submitFreeTrialBooking = async (
    teacherId: number,
    startUtc: string,
    endUtc: string,
  ) => {
    await confirmFreeTrialBookingApi({
      attendeeId: state.attendeeId,
      categoryId: state.categoryId,
      languageCode: state.languageCode,
      hearAboutUsId: state.hearAboutUsId,
      hearAboutUsOther: isHearAboutUsOther
        ? state.hearAboutUsOther.trim()
        : undefined,
      teacherId,
      startUtc,
      endUtc,
      locale,
    });

    // The dashboard and "All Lectures" pages are client components that
    // only fetch on mount - Next's Router Cache can otherwise reuse an
    // already-visited instance of those routes and never re-run that
    // fetch, so the newly booked lecture wouldn't show up until a hard
    // refresh. router.refresh() invalidates the cache for every route so
    // the next visit to either page re-fetches fresh data.
    router.refresh();

    // GTM conversion, reported defensively: the booking is confirmed by this
    // point, so nothing here can delay or block it. Sits in this function rather than
    // handleConfirm() so the retry after an accepted "slot taken" suggestion
    // reports too - both paths come through here, and only once the backend
    // has actually confirmed. Category and language are sent as display
    // labels so the container needs no lookup table, falling back to the raw
    // id if the option lists have since been refetched.
    try {
      pushGtmFreeTrialBooking({
        user,
        category:
          categories.find((option) => option.value === state.categoryId)
            ?.label ?? state.categoryId,
        language:
          languages.find((option) => option.value === state.languageCode)
            ?.label ?? state.languageCode,
        teacherId,
        startUtc,
        endUtc,
      });
    } catch {
      /* the booking itself already succeeded - never surface this */
    }

    // The confirmed state gets its own URL (REQ-2). The success page renders
    // only with the proof this leaves behind, so no other action can produce
    // that URL. The Schedule conversion was recorded by the backend inside the
    // confirm call above; the flush has the mounted MetaPixel claim and fire
    // it - once, so a refresh of the success page fires nothing.
    completeFreeTrialBooking(
      (href) => router.push(href),
      localizedHref(FREE_TRIAL_SUCCESS_PATH),
      requestMetaPixelFlush,
    );
  };

  const handleConfirm = async () => {
    if (!state.selectedSlot || hasActiveFreeTrial) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await submitFreeTrialBooking(
        state.selectedSlot.teacher_id,
        state.selectedSlot.start_utc,
        state.selectedSlot.end_utc,
      );
    } catch (error: any) {
      const suggestedSlot = error?.response?.data?.suggested_slot;

      // The teacher was no longer available at the requested slot by the
      // time this confirm request landed (either it fell outside their
      // lead time or someone else took it first) - the backend responds
      // with the same teacher's next bookable slot instead of a dead-end
      // error, so offer it here rather than only showing a generic failure.
      if (
        error?.response?.status === 409 &&
        suggestedSlot?.start_utc &&
        suggestedSlot?.end_utc
      ) {
        const choice = await Swal.fire({
          icon: "info",
          title: t("slot_taken_title"),
          text: t("slot_taken_message", {
            day: suggestedSlot.day_label,
            time: suggestedSlot.time_label,
          }),
          showDenyButton: true,
          confirmButtonText: t("book_suggested_slot"),
          denyButtonText: t("cancel"),
          confirmButtonColor: "var(--primary)",
          denyButtonColor: "var(--text-secondary)",
        });

        if (choice.isConfirmed) {
          try {
            await submitFreeTrialBooking(
              suggestedSlot.teacher_id,
              suggestedSlot.start_utc,
              suggestedSlot.end_utc,
            );
          } catch (retryError: any) {
            await Swal.fire({
              icon: "error",
              title: t("booking_failed_title"),
              text: retryError?.response?.data?.message || t("booking_failed"),
              confirmButtonText: t("choose_another_slot"),
              confirmButtonColor: "var(--primary)",
            });
            handlePrevious();
          }
        } else {
          handlePrevious();
        }

        return;
      }

      await Swal.fire({
        icon: "error",
        title: t("booking_failed_title"),
        text: error?.response?.data?.message || t("booking_failed"),
        confirmButtonText: t("choose_another_slot"),
        confirmButtonColor: "var(--primary)",
      });
      handlePrevious();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Still finding out who this is - nothing to decide yet.
  if (userLoading) {
    return (
      <main className="min-h-screen bg-table py-16">
        <div className="mx-auto max-w-[1200px] px-4">
          <LottieLoader centered className="py-20" label={t("loading_user")} />
        </div>
      </main>
    );
  }

  // Logged out: the redirect above is already in flight, so say where they are
  // going rather than flashing a booking form they cannot use.
  if (isUnauthenticated) {
    return (
      <main className="min-h-screen bg-table py-16">
        <div className="mx-auto max-w-[1200px] px-4">
          <LottieLoader
            centered
            className="py-20"
            label={t("redirecting_to_register")}
          />
        </div>
      </main>
    );
  }

  return (
    <>
      <BookingSessionTimer
        isActive={!isUnauthenticated && !userLoading}
        onTimeout={handleSessionTimeout}
        // Free-trial booking's own session window - 10 minutes, matching the
        // legacy booking-session-timer.js this page was ported from. The
        // shared component's 15-minute default stays untouched for paid
        // course checkout (CourseBookingContent), which doesn't pass this
        // prop and instead drives its own countdown off a server-issued
        // expiresAt.
        timeoutSeconds={10 * 60}
        timerLabel={t("timer_label")}
        timeoutTitle={t("session_timeout_title")}
        timeoutMessage={t("session_timeout_message")}
        confirmButtonText={t("previous")}
      />
      <main className="relative min-h-screen py-8 pb-[80px] md:py-10 sm:pb-0">
        <div
          className="hidden lg:block fixed inset-0 -z-10 bg-no-repeat bg-top bg-contain"
          style={{
            backgroundImage: "url('/f/images/trialbg.png')",
          }}
        />
        <div className="mx-auto flex max-w-[1000px] flex-col gap-4 px-4">
          <section className="overflow-hidden">
            <div className="inline-block text-secondary rounded-[10px]">
              <h1 className="text-2xl font-bold md:text-4xl">{t("title")}</h1>
            </div>
          </section>

          {/* <section className="grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map((item) => {
              const itemStep = item as BookingStep;
              const isActive = step === itemStep;
              const isDone = step > itemStep;

              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    if (itemStep < step) {
                      setStep(itemStep);
                    }
                  }}
                  className={`cursor-pointer rounded-[24px] border px-5 py-4 text-start transition ${
                    isActive
                      ? "border-secondary bg-white shadow-sm"
                      : isDone
                        ? "border-status-info/20 bg-status-info/10"
                        : "border-border bg-white/70"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold ${
                        isActive
                          ? "bg-primary text-on-primary"
                          : isDone
                            ? "bg-secondary text-on-secondary"
                            : "bg-hover text-text-secondary"
                      }`}
                    >
                      {item}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-primary">
                        {t(`steps.${item}.title`)}
                      </p>
                      <p className="text-xs text-text-secondary">
                        {t(`steps.${item}.description`)}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </section> */}

          <section ref={stepContentRef}>
            {metadataLoading ? (
              <LottieLoader
                centered
                className="py-20"
                label={t("loading_metadata")}
              />
            ) : metadataError ? (
              <div className="rounded-[10px] border border-danger/20 bg-danger/10 p-4 text-danger">
                {metadataError}
              </div>
            ) : (
              <>
                {step === 1 ? (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold text-primary md:text-2xl">
                        {t("steps.1.heading")}
                      </h2>
                    </div>

                    {/* Every field below is disabled while the selected
                      attendee has a trial in progress. The modal used to be
                      the only place that said so, which left the form
                      silently dead once it was dismissed - or never opened,
                      as on first load. */}
                    {hasActiveFreeTrial ? (
                      <div className="flex items-start gap-3 rounded-[10px] border border-status-info/20 bg-status-info/10 p-4">
                        <Calendar className="mt-0.5 h-5 w-5 shrink-0 text-status-info" />
                        <div className="text-sm font-medium leading-relaxed text-text-secondary">
                          <p>{t("active_trial_body")}</p>
                          <p className="mt-1">{t("active_trial_note")}</p>
                        </div>
                      </div>
                    ) : null}

                    {/* One grid for the whole step: the fields Google could not
                        supply are interleaved with the booking questions rather
                        than sitting in a block of their own, so the student sees
                        one short form instead of two. */}
                    <FreeTrialProfileFields
                      ref={profileFieldsRef}
                      missingFields={
                        needsProfile && Array.isArray(user?.missing_fields)
                          ? (user.missing_fields as string[])
                          : []
                      }
                      // The user is reloaded by handleNext, which awaits it
                      // before step 2 can request any slots.
                      onCompleted={() => {}}
                    >
                      {({ failed, fields: profileFields }) => (
                        <>
                          {failed}
                          <div className="grid grid-cols-12 gap-5">

                      <div className="col-span-12 md:col-span-6">
                      <FormSelect
                        label={t("fields.attendee")}
                        options={attendeeOptions}
                        value={state.attendeeId}
                        onChange={(value) => {
                          if (value === "ADD_DEPENDANT") {
                            setIsAddModalOpen(true);
                          } else if (value === state.attendeeId) {
                            // Re-picking the person who is already selected
                            // asks the same question again, so answer it
                            // again - straight from the flag the last fetch
                            // for them produced. Nothing in state changed, so
                            // the effect below won't re-run, and forcing it to
                            // would swap the whole form for the loader.
                            setShowActiveTrialModal(hasActiveFreeTrial);
                          } else {
                            // Marks this pick as the one the modal may answer,
                            // once the metadata for it comes back.
                            attendeeAwaitingModalRef.current = value;
                            updateField("attendeeId", value);
                          }
                        }}
                        placeholder={t("placeholders.attendee")}
                        error={errors.attendeeId}
                        isSearchable={!isMobile}
                      />
                      </div>

                      {profileFields.gender ? (
                        <div className="col-span-12 md:col-span-6">{profileFields.gender}</div>
                      ) : null}

                      {/* Only shown when Google gave a name the API would
                          reject (Arabic, or under three letters). An email
                          registrant supplied theirs on the first page. */}
                      {profileFields.fname ? (
                        <div className="col-span-12 md:col-span-6">{profileFields.fname}</div>
                      ) : null}

                      {profileFields.lname ? (
                        <div className="col-span-12 md:col-span-6">{profileFields.lname}</div>
                      ) : null}

                      {profileFields.phone ? (
                        <div className="col-span-12 md:col-span-6">{profileFields.phone}</div>
                      ) : null}

                      {profileFields.referralCode ? (
                        <div className="col-span-12 md:col-span-6">{profileFields.referralCode}</div>
                      ) : null}

                      {profileFields.birthday ? (
                        <div className="col-span-12 md:col-span-6">{profileFields.birthday}</div>
                      ) : null}

                      {profileFields.countryTimezone ? (
                        <div className="col-span-12 md:col-span-6">{profileFields.countryTimezone}</div>
                      ) : null}

                      <div className="col-span-12">
                      <FormSelect
                        label={t("fields.category")}
                        options={
                          inOtherView
                            ? [
                                // Return to the primary list.
                                {
                                  value: BACK_CATEGORY_VALUE,
                                  label: t("fields.category_back"),
                                },
                                ...otherCategories.map((cat) => ({
                                  ...cat,
                                  label: cat.isBooked
                                    ? `${cat.label} (${t("booked")})`
                                    : cat.label,
                                  isDisabled: cat.isBooked,
                                })),
                              ]
                            : [
                                ...primaryCategories.map((cat) => ({
                                  ...cat,
                                  label: cat.isBooked
                                    ? `${cat.label} (${t("booked")})`
                                    : cat.label,
                                  isDisabled: cat.isBooked,
                                })),
                                // Drill into the Other group in the same
                                // select. The trailing chevron signals it opens
                                // a sub-list rather than being a leaf category.
                                // "›" is a bidi-mirrored glyph, so under the
                                // app's dir="rtl" (Arabic) it renders pointing
                                // left on its own — a locale branch would
                                // double-flip it and point the wrong way.
                                ...(otherCategories.length > 0
                                  ? [
                                      {
                                        value: OTHER_CATEGORY_VALUE,
                                        label: `${t("fields.category_other")} ›`,
                                      },
                                    ]
                                  : []),
                              ]
                        }
                        // In either view the selected value is the real
                        // categoryId (or "" → placeholder); the sentinels are
                        // transient actions handled in onChange, never a value.
                        value={state.categoryId}
                        // Controlled menu + closeMenuOnSelect=false so drilling
                        // into Other (or going Back) keeps the dropdown open and
                        // the user immediately sees the swapped option list;
                        // only picking a real category closes it.
                        closeMenuOnSelect={false}
                        menuIsOpen={categoryMenuOpen}
                        onMenuOpen={() => setCategoryMenuOpen(true)}
                        onMenuClose={() => setCategoryMenuOpen(false)}
                        onChange={(value) => {
                          if (value === OTHER_CATEGORY_VALUE) {
                            setOtherCategoryOpen(true);
                            setCategoryMenuOpen(true);
                            return;
                          }
                          if (value === BACK_CATEGORY_VALUE) {
                            // Leave the Other group and clear any Other selection
                            // so the primary list shows again, still open.
                            setOtherCategoryOpen(false);
                            setCategoryIds([]);
                            setCategoryMenuOpen(true);
                            return;
                          }
                          setOtherCategoryOpen(false);
                          setCategoryIds(value ? [value] : []);
                          setCategoryMenuOpen(false);
                        }}
                        placeholder={
                          inOtherView
                            ? t("placeholders.other_category")
                            : t("placeholders.category")
                        }
                        error={errors.categoryId}
                        isDisabled={hasActiveFreeTrial}
                        isSearchable={!isMobile}
                      />
                      </div>

                      <div className="col-span-12 md:col-span-6">
                      <FormSelect
                        label={t("fields.language")}
                        options={languages}
                        value={state.languageCode}
                        onChange={(value) => updateField("languageCode", value)}
                        placeholder={
                          !state.categoryId
                            ? t("placeholders.language_pick_category_first")
                            : languagesLoading
                              ? t("placeholders.language_loading")
                              : languages.length === 0
                                ? t("placeholders.language_none")
                                : t("placeholders.language")
                        }
                        error={errors.languageCode}
                        isLoading={languagesLoading}
                        isDisabled={
                          hasActiveFreeTrial ||
                          !state.categoryId ||
                          languagesLoading ||
                          languages.length === 0
                        }
                        isSearchable={!isMobile}
                      />
                      </div>

                      <div className="col-span-12 md:col-span-6">
                      <div>
                        <FormSelect
                          label={t("fields.hear_about_us")}
                          options={hearAboutUsOptions}
                          value={state.hearAboutUsId}
                          onChange={(value) =>
                            updateField("hearAboutUsId", value)
                          }
                          placeholder={t("placeholders.hear_about_us")}
                          error={errors.hearAboutUsId}
                          isDisabled={hasActiveFreeTrial}
                          isSearchable={!isMobile}
                        />

                        {isHearAboutUsOther ? (
                          <div className="mt-4">
                            <label className="mb-1.5 block text-lg text-primary">
                              {t("fields.hear_about_us_other")}
                            </label>
                            <input
                              type="text"
                              value={state.hearAboutUsOther}
                              onChange={(e) =>
                                updateField("hearAboutUsOther", e.target.value)
                              }
                              placeholder={t(
                                "placeholders.hear_about_us_other",
                              )}
                              disabled={hasActiveFreeTrial}
                              className="w-full rounded-md border border-border p-3 text-sm outline-none focus:border-secondary focus:ring-1 focus:ring-secondary disabled:cursor-not-allowed disabled:bg-hover disabled:opacity-60"
                            />
                            {errors.hearAboutUsOther ? (
                              <p className="mt-1 text-sm text-danger">
                                {errors.hearAboutUsOther}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      </div>

                      {profileFields.terms ? (
                        <div className="col-span-12">{profileFields.terms}</div>
                      ) : null}

                    </div>
                        </>
                      )}
                    </FreeTrialProfileFields>

                  </div>
                ) : null}

                {step === 2 && isProfileBlocked ? (
                  <div
                    role="alert"
                    className="rounded-[10px] border border-danger/20 bg-danger/10 p-4 text-sm text-danger"
                  >
                    {t("complete_profile_first")}
                  </div>
                ) : null}

                {step === 2 && !isProfileBlocked ? (
                  <div className="space-y-6">
                    <div className="">
                      <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-base font-semibold text-primary">
                          {t("choose_day")}
                        </h3>
                        <button
                          type="button"
                          onClick={() =>
                            setCalendarView((current) =>
                              current === "strip" ? "month" : "strip",
                            )
                          }
                          className="flex items-center gap-2 rounded-[10px] bg-primary px-6 py-2.5 text-sm font-semibold text-on-primary hover:opacity-90 transition-opacity duration-150 min-w-[150px] justify-center"
                        >
                          <Calendar className="h-4 w-4 shrink-0" />
                          {t(
                            calendarView === "strip"
                              ? "full_month_view"
                              : "day_view",
                          )}
                        </button>
                      </div>

                      {calendarView === "strip" && earliestStripDate ? (
                        <div className="mb-4 flex items-center gap-3 rounded-[10px] bg-amber-50 p-4">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white shadow-sm">
                            <Calendar className="h-4 w-4 text-amber-500" />
                          </div>
                          <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
                            <span className="font-bold text-text-primary">
                              {t("earliest_available")}:{" "}
                              {isSameDay(earliestStripDate, minDate)
                                ? t("today")
                                : format(earliestStripDate, "EEE, MMM d", {
                                    locale: dateLocale,
                                  })}
                              {(() => {
                                const firstSlot = dayStripSlots[
                                  dayKey(earliestStripDate)
                                ]?.reduce<FreeTrialSlot | null>(
                                  (earliest, slot) =>
                                    !earliest || slot.start < earliest.start
                                      ? slot
                                      : earliest,
                                  null,
                                );
                                return firstSlot
                                  ? ` ${t("at_time")} ${formatRawTimeString(firstSlot.start, locale)}`
                                  : "";
                              })()}
                            </span>
                            {isSameDay(earliestStripDate, minDate) ? (
                              <span className="text-text-secondary">
                                {t("earliest_available_today")}
                              </span>
                            ) : null}
                          </p>
                        </div>
                      ) : null}

                      {calendarView === "strip" ? (
                        <div className="flex flex-col items-center gap-3">
                          <div className="flex w-full items-center justify-center gap-4">
                            <button
                              type="button"
                              aria-label="previous"
                              disabled={dayStripIndex <= 0}
                              onClick={() =>
                                selectStripDay(Math.max(0, dayStripIndex - 1))
                              }
                              className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-primary disabled:opacity-30"
                            >
                              {isArabic ? "›" : "‹"}
                            </button>

                            <button
                              type="button"
                              onClick={() => selectStripDay(dayStripIndex)}
                              className={`flex min-w-[260px] flex-col items-center gap-1 rounded-2xl px-14 py-7 text-center transition ${
                                dayKey(stripDates[dayStripIndex]) ===
                                (state.selectedDate
                                  ? dayKey(state.selectedDate)
                                  : "")
                                  ? "bg-primary text-on-primary"
                                  : "bg-table text-text-primary ring-1 ring-border"
                              }`}
                            >
                              <span className="text-sm font-medium">
                                {format(stripDates[dayStripIndex], "EEE", {
                                  locale: dateLocale,
                                })}
                              </span>
                              <span className="text-xl font-bold">
                                {format(stripDates[dayStripIndex], "d MMM", {
                                  locale: dateLocale,
                                })}
                              </span>
                            </button>

                            <button
                              type="button"
                              aria-label="next"
                              disabled={dayStripIndex >= stripDates.length - 1}
                              onClick={() =>
                                selectStripDay(
                                  Math.min(
                                    stripDates.length - 1,
                                    dayStripIndex + 1,
                                  ),
                                )
                              }
                              className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-primary disabled:opacity-30"
                            >
                              {isArabic ? "‹" : "›"}
                            </button>
                          </div>

                          <div className="flex items-center gap-1.5">
                            {stripDates.map((date, index) => (
                              <span
                                key={dayKey(date)}
                                className={`h-1.5 w-1.5 rounded-full ${
                                  index === dayStripIndex
                                    ? "bg-primary"
                                    : "bg-border"
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <CalendarPicker
                            required
                            selected={state.selectedDate ?? undefined}
                            onSelect={(date: any) => {
                              if (date) {
                                updateField("selectedDate", date);
                                setTimeout(() => {
                                  slotsRef.current?.scrollIntoView({
                                    behavior: "smooth",
                                    block: "start",
                                  });
                                }, 100);
                              }
                            }}
                            disabled={[
                              { before: minDate },
                              { after: maxDate },
                              (date: Date) =>
                                monthAvailability?.[dayKey(date)] === false,
                            ]}
                            modifiers={{
                              available: (date: Date) =>
                                date >= minDate &&
                                date <= maxDate &&
                                monthAvailability?.[dayKey(date)] !== false,
                            }}
                            modifiersClassNames={{
                              available:
                                "text-black rounded-full hover:bg-success/20",
                            }}
                            locale={dateLocale}
                            dir={isArabic ? "rtl" : "ltr"}
                            todayLabel={t("today")}
                          />
                        </div>
                      )}

                      {errors.selectedDate ? (
                        <p className="mt-4 text-sm text-danger">
                          {errors.selectedDate}
                        </p>
                      ) : null}

                      <div className="mt-8" ref={slotsRef}>
                        <h3 className="text-lg font-semibold text-primary">
                          {t("slots_title")}
                        </h3>
                        {state.selectedDate ? (
                          <p className="mt-1 text-sm text-text-secondary">
                            {t("slots_helper_for_date", {
                              date: format(
                                state.selectedDate,
                                "EEE, MMMM do, yyyy",
                                {
                                  locale: dateLocale,
                                },
                              ),
                            })}
                          </p>
                        ) : null}

                        {!state.selectedDate ? (
                          <div className="mt-4 rounded-[10px] border border-dashed border-border bg-table p-4 text-sm text-text-secondary">
                            {t("select_date_first")}
                          </div>
                        ) : slotsLoading ? (
                          <LottieLoader
                            centered
                            className="py-10"
                            label={t("loading_slots")}
                          />
                        ) : slotsError ? (
                          <div className="mt-4 rounded-[10px] border border-danger/20 bg-danger/10 p-4 text-sm text-danger">
                            {slotsError}
                          </div>
                        ) : (
                          <div className="mt-4 gap-3 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5">
                            {slots.map((slot) => {
                              const isSelected =
                                state.selectedSlot?.start === slot.start &&
                                state.selectedSlot?.end === slot.end;

                              return (
                                <button
                                  key={`${slot.start}-${slot.end}`}
                                  type="button"
                                  disabled={slot.isReserved}
                                  onClick={() =>
                                    updateField("selectedSlot", slot)
                                  }
                                  className={`cursor-pointer rounded-[10px] border px-4 py-3 text-sm font-medium transition ${
                                    slot.isReserved
                                      ? "cursor-not-allowed border-border bg-hover text-text-muted"
                                      : isSelected
                                        ? "bg-success text-on-success"
                                        : "border-secondary text-secondary"
                                  }`}
                                >
                                  <span className="block">
                                    {formatRawTimeString(slot.start, locale)} -{" "}
                                    {formatRawTimeString(slot.end, locale)}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {!slotsLoading &&
                        state.selectedDate &&
                        !slotsError &&
                        slots.length === 0 ? (
                          <p className="mt-4 text-sm text-text-secondary">
                            {t("no_slots")}
                          </p>
                        ) : null}

                        {errors.selectedSlot ? (
                          <p className="mt-4 text-sm text-danger">
                            {errors.selectedSlot}
                          </p>
                        ) : null}

                        <div className="mt-4 inline-flex items-center gap-2 rounded-[10px] bg-status-info/10 px-4 py-2 text-sm font-bold text-primary ring-1 ring-status-info/20">
                          <span className="text-secondary">
                            <Globe className="h-4 w-4" />
                          </span>{" "}
                          {attendeeTimezone}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {step === 3 ? (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-semibold text-primary">
                        {t("steps.3.heading")}
                      </h2>
                      <p className="mt-2 text-sm text-text-secondary">
                        {t("steps.3.helper")}
                      </p>
                    </div>

                    <div className="relative grid rounded-2xl border border-border bg-white p-5 shadow-sm gap-y-2 md:grid-cols-2 md:gap-x-24 md:p-6 md:after:absolute md:after:bottom-6 md:after:left-1/2 md:after:top-6 md:after:w-px md:after:bg-border">
                      {" "}
                      <div className="flex flex-col items-start gap-1 py-2 md:flex-row md:items-center md:justify-between md:gap-3">
                        <span className="text-sm font-semibold text-text-secondary">
                          {t("fields.attendee")}
                        </span>
                        <span className="text-sm font-medium text-primary">
                          {selectedAttendee?.label ?? t("not_selected")}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-1 py-2 md:flex-row md:items-center md:justify-between md:gap-3">
                        <span className="text-sm font-semibold text-text-secondary">
                          {t("fields.category")}
                        </span>
                        <span className="text-sm font-medium text-primary">
                          {selectedCategory?.label ?? t("not_selected")}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-1 py-2 md:flex-row md:items-center md:justify-between md:gap-3">
                        <span className="text-sm font-semibold text-text-secondary">
                          {t("fields.language")}
                        </span>
                        <span className="text-sm font-medium text-primary">
                          {selectedLanguage?.label ?? t("not_selected")}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-1 py-2 md:flex-row md:items-center md:justify-between md:gap-3">
                        <span className="text-sm font-semibold text-text-secondary">
                          {t("fields.hear_about_us")}
                        </span>
                        <span className="text-sm font-medium text-primary">
                          {isHearAboutUsOther && state.hearAboutUsOther.trim()
                            ? state.hearAboutUsOther.trim()
                            : (selectedHearAboutUs?.label ?? t("not_selected"))}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-1 py-2 md:flex-row md:items-center md:justify-between md:gap-3">
                        <span className="text-sm font-semibold text-text-secondary">
                          {t("fields.date")}
                        </span>
                        <span className="text-sm font-medium text-primary">
                          {state.selectedDate
                            ? format(state.selectedDate, "PPP", {
                                locale: dateLocale,
                              })
                            : t("not_selected")}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-1 py-2 md:flex-row md:items-center md:justify-between md:gap-3">
                        <span className="text-sm font-semibold text-text-secondary">
                          {t("fields.slot")}
                        </span>
                        <span className="text-sm font-medium text-primary">
                          {state.selectedSlot
                            ? `${formatRawTimeString(state.selectedSlot.start, locale)} - ${formatRawTimeString(
                                state.selectedSlot.end,
                                locale,
                              )}`
                            : t("not_selected")}
                        </span>
                      </div>
                    </div>

                    {errors.selectedSlot && (
                      <div className="rounded-[10px] border border-danger/20 bg-danger/10 p-4 text-sm text-danger">
                        {errors.selectedSlot}
                      </div>
                    )}
                  </div>
                ) : null}

                {/* Navigation Buttons */}
                <div className="fixed bottom-0 left-0 right-0 z-30 mt-4 px-4 py-3 bg-card/95 backdrop-blur-sm border-t border-border-subtle shadow-[0_-4px_12px_rgba(0,0,0,0.05)] sm:static sm:z-auto sm:mt-8 sm:left-auto sm:right-auto sm:px-0 sm:py-0 sm:bg-transparent sm:backdrop-blur-none sm:border-t-0 sm:shadow-none sm:pt-6">
                  <div className="flex w-full flex-row items-center gap-3 sm:justify-end sm:gap-4">
                    {step > 1 ? (
                      <button
                        type="button"
                        onClick={handlePrevious}
                        disabled={isSubmitting}
                        className="flex flex-1 sm:flex-initial w-full sm:w-auto sm:min-w-[160px] cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-border px-4 py-3.5 sm:px-6 sm:py-4 font-semibold text-primary transition-colors hover:border-primary hover:bg-table disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isArabic ? (
                          <ChevronRight size={18} />
                        ) : (
                          <ChevronLeft size={18} />
                        )}
                        {t("previous")}
                      </button>
                    ) : (
                      // placeholder so Next stays right-aligned on step 1
                      <div
                        className="hidden sm:block flex-1 sm:flex-initial sm:min-w-[160px]"
                        aria-hidden="true"
                      />
                    )}

                    {step < 3 ? (
                      <button
                        type="button"
                        onClick={() => void handleNext()}
                        disabled={hasActiveFreeTrial || isPreparingSlots}
                        className="group/next flex flex-1 sm:flex-initial w-full sm:w-auto sm:min-w-[240px] cursor-pointer items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3.5 sm:px-8 sm:py-4 text-base font-bold text-on-primary shadow-sm transition-all hover:bg-secondary hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-primary disabled:hover:shadow-sm"
                      >
                        {isPreparingSlots ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        ) : null}
                        {t("next")}
                        {isPreparingSlots ? null : isArabic ? (
                          <ChevronLeft
                            size={18}
                            className="transition-transform duration-200 group-hover/next:-translate-x-1"
                          />
                        ) : (
                          <ChevronRight
                            size={18}
                            className="transition-transform duration-200 group-hover/next:translate-x-1"
                          />
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={isSubmitting || hasActiveFreeTrial}
                        onClick={handleConfirm}
                        className="flex flex-1 sm:flex-initial w-full sm:w-auto sm:min-w-[240px] cursor-pointer items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3.5 sm:px-8 sm:py-4 text-base font-bold text-on-primary shadow-sm transition-all hover:bg-secondary hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-primary disabled:hover:shadow-sm"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            {t("confirming")}
                          </>
                        ) : (
                          t("confirm_booking")
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </main>

      <AddDependantModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={(newDep) => {
          if (newDep && newDep.id) {
            updateField("attendeeId", String(newDep.id));
          }
        }}
      />

      {/* Active Free Trial Modal — this attendee already has a free trial
          that isn't Missed (upcoming, or resolved some other way), so a new
          one can't be booked until it's missed or completed. */}
      {showActiveTrialModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-backdrop transition-opacity animate-in fade-in duration-200"
          aria-labelledby="active-free-trial-modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="fixed inset-0"
            onClick={() => setShowActiveTrialModal(false)}
          />

          <div className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl bg-modal p-6 text-left shadow-2xl transition-all border border-border">
            <button
              type="button"
              onClick={() => setShowActiveTrialModal(false)}
              className="absolute top-4 end-4 text-text-muted hover:text-foreground transition-colors p-1 rounded-full hover:bg-hover"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-status-info/10 text-status-info">
                <Calendar className="h-7 w-7" />
              </div>

              <h3
                id="active-free-trial-modal-title"
                className="text-xl font-bold text-primary mb-2"
              >
                {t("active_trial_title")}
              </h3>

              <p className="mb-4 text-sm font-medium leading-relaxed text-text-secondary text-start w-full">
                {t("active_trial_body")}
              </p>

              {/* The follow-up sits in its own panel: it's the reassuring
                half of the message ("this isn't permanent"), not part of
                the statement above it. */}
              <div className="w-full rounded-xl bg-status-info/10 border-s-4 border-status-info p-4 mb-6 text-start">
                <p className="text-sm font-medium text-text-secondary leading-relaxed">
                  {t("active_trial_note")}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowActiveTrialModal(false)}
                className="rounded-xl bg-primary px-10 py-3 text-sm font-bold text-on-primary transition hover:bg-secondary cursor-pointer shadow-sm"
              >
                {t("active_trial_close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SummaryCard({ title, value }: { title: string; value: string }) {
  return (
    <div className="rounded-[24px] border border-status-info/20 bg-card p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-text-muted">
        {title}
      </p>
      <p className="mt-3 text-base font-semibold text-primary">{value}</p>
    </div>
  );
}

function resolveId(value: Record<string, unknown>): string {
  return typeof value.id === "number" ? String(value.id) : "";
}

function resolveDisplayName(value: Record<string, unknown>): string {
  if (typeof value.name === "string" && value.name.trim().length > 0) {
    return value.name.trim();
  }

  const fname = typeof value.fname === "string" ? value.fname.trim() : "";
  const lname = typeof value.lname === "string" ? value.lname.trim() : "";
  const fullName = `${fname} ${lname}`.trim();

  return fullName || "—";
}

/**
 * The backend's refusal to list slots for an attendee whose profile is still
 * missing data teacher matching depends on (a 422 keyed on `profile`).
 */
function isProfileIncompleteError(error: unknown): boolean {
  if (!isRecord(error) || !isRecord(error.response)) {
    return false;
  }

  const { status, data } = error.response;

  return (
    status === 422 &&
    isRecord(data) &&
    isRecord(data.errors) &&
    "profile" in data.errors
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
